<template>
  <div>
    <div class="d-flex align-items-center mb-3">
      <router-link
        :to="{ name: 'Frauds' }"
        class="btn btn-sm btn-outline-dark mr-3"
      >
        <CIcon
          name="cil-arrow-left"
          size="sm"
          class="mr-1"
        />
        Back
      </router-link>
      <h2 class="mb-0">
        Flagged Call Details
      </h2>
    </div>

    <div
      v-if="loading"
      class="text-center p-5"
    >
      <CSpinner color="primary" />
    </div>

    <div
      v-else-if="error"
      class="text-center text-danger p-5"
    >
      {{ error }}
    </div>

    <div v-else-if="detail">
      <!-- Call Info -->
      <CCard class="mb-3 shadow-sm">
        <CCardHeader class="bg-white">
          <strong>Call Info</strong>
        </CCardHeader>
        <CCardBody>
          <div class="row">
            <div class="col-md-6">
              <dl class="row mb-0">
                <dt class="col-sm-4">
                  Call ID
                </dt>
                <dd class="col-sm-8 text-monospace small">
                  {{ detail.callId }}
                </dd>
                <dt class="col-sm-4">
                  Caller ID
                </dt>
                <dd class="col-sm-8">
                  {{ detail.callerId }}
                </dd>
                <dt class="col-sm-4">
                  Received At
                </dt>
                <dd class="col-sm-8">
                  {{ formatDateTime(detail.receivedAt) }}
                </dd>
                <dt class="col-sm-4">
                  Type
                </dt>
                <dd class="col-sm-8">
                  <CBadge :class="detail.flagCategory === 'Fraud' || detail.flagCategory === 0 ? 'badge-fraud-count' : 'badge-suspicious-count'">
                    {{ detail.flagCategory === 0 ? 'Fraud' : detail.flagCategory === 1 ? 'Suspicious' : detail.flagCategory }}
                  </CBadge>
                </dd>
                <dt class="col-sm-4">
                  Filter Tags
                </dt>
                <dd class="col-sm-8">
                  <CBadge
                    v-for="tag in splitTags(detail.filterTags)"
                    :key="tag"
                    color="secondary"
                    class="mr-1"
                    :title="tagDescription(tag)"
                  >
                    {{ formatTag(tag) }}
                  </CBadge>
                </dd>
              </dl>
            </div>
            <div class="col-md-6">
              <dl class="row mb-0">
                <dt class="col-sm-4">
                  Seller
                </dt>
                <dd class="col-sm-8">
                  {{ detail.sellerName }}
                </dd>
                <dt class="col-sm-4">
                  Buyer
                </dt>
                <dd class="col-sm-8">
                  {{ detail.buyerName }}
                </dd>
                <dt class="col-sm-4">
                  Campaign
                </dt>
                <dd class="col-sm-8">
                  {{ detail.campaignId }}
                </dd>
                <dt class="col-sm-4">
                  Category
                </dt>
                <dd class="col-sm-8">
                  {{ detail.categoryName }}
                </dd>
                <dt class="col-sm-4">
                  Traffic Source
                </dt>
                <dd class="col-sm-8">
                  {{ detail.trafficSourceName }}
                </dd>
                <dt class="col-sm-4">
                  Seller Offer
                </dt>
                <dd class="col-sm-8">
                  {{ detail.sellerOfferName }}
                </dd>
              </dl>
            </div>
          </div>
        </CCardBody>
      </CCard>

      <!-- Analytics Data -->
      <div v-if="detail.analyticsData">
        <!-- Summary & Notes -->
        <CCard
          v-if="detail.analyticsData.summary || detail.analyticsData.generalNotes"
          class="mb-3 shadow-sm"
        >
          <CCardHeader class="bg-white">
            <strong>Summary & Notes</strong>
          </CCardHeader>
          <CCardBody>
            <div v-if="detail.analyticsData.summary">
              <h6>Summary</h6>
              <p class="text-muted">
                {{ detail.analyticsData.summary }}
              </p>
            </div>
            <div v-if="detail.analyticsData.generalNotes">
              <h6>General Notes</h6>
              <p class="text-muted">
                {{ detail.analyticsData.generalNotes }}
              </p>
            </div>
          </CCardBody>
        </CCard>

        <CCard
          v-for="section in analyticsSections"
          :key="section.title"
          class="mb-3 shadow-sm"
        >
          <CCardHeader class="bg-white">
            <strong>{{ section.title }}</strong>
          </CCardHeader>
          <CCardBody>
            <dl class="row mb-0">
              <template v-for="field in section.fields">
                <dt
                  :key="field.label + '-dt'"
                  class="col-sm-4"
                >
                  {{ field.label }}
                </dt>
                <dd
                  :key="field.label + '-dd'"
                  class="col-sm-8"
                >
                  <template v-if="Array.isArray(field.value)">
                    <span
                      v-if="field.value.length === 0"
                      class="text-muted"
                    >-</span>
                    <CBadge
                      v-for="(item, idx) in field.value"
                      :key="idx"
                      color="light"
                      class="mr-1 text-dark border"
                    >
                      {{ item }}
                    </CBadge>
                  </template>
                  <CBadge
                    v-else-if="typeof field.value === 'boolean'"
                    :color="field.value ? 'success' : 'secondary'"
                  >
                    {{ field.value ? 'Yes' : 'No' }}
                  </CBadge>
                  <span
                    v-else-if="field.value === null || field.value === undefined || field.value === ''"
                    class="text-muted"
                  >-</span>
                  <span v-else>{{ field.value }}</span>
                </dd>
              </template>
            </dl>
          </CCardBody>
        </CCard>
      </div>

      <div
        v-else
        class="text-center text-muted p-4"
      >
        No analytics data available for this call.
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import moment from 'moment-timezone'
import { showErrorMessage } from '@/notification-utils'
import FILTER_TAG_DESCRIPTIONS from './filterTagDescriptions'

export default {
  name: 'FraudDetails',

  title: 'Flagged Call Details',

  data() {
    return {
      loading: false,
      error: null,
      detail: null,
    }
  },

  computed: {
    analyticsSections() {
      const d = this.detail?.analyticsData
      if (!d) {
        return []
      }

      const sections = []

      if (d.fraud) {
        sections.push({
          title: 'Fraud',
          fields: [
            { label: 'Fraud Detected', value: d.fraud.fraudDetected },
            { label: 'AI Voice Suspected', value: d.fraud.aiVoiceSuspected },
            { label: 'Acoustic Risk Score', value: d.fraud.acousticRiskScore },
            { label: 'Multi Signal Risk Score', value: d.fraud.multiSignalRiskScore },
            { label: 'Fraud Reasoning', value: d.fraud.fraudReasoning },
            { label: 'Fraud Phrases', value: d.fraud.fraudPhrases },
            { label: 'Suspicious Phrase Bucket', value: d.fraud.suspiciousPhraseBucket },
            { label: 'Callback Arbitrage Detected', value: d.fraud.callbackArbitrageDetected },
            { label: 'Agent Coaching Detected', value: d.fraud.agentCoachingDetected },
            { label: 'Payment Method Detected', value: d.fraud.paymentMethodDetected },
            { label: 'Impersonation Detected', value: d.fraud.impersonationDetected },
            { label: 'Scareware/Urgency Lure', value: d.fraud.scarewareUrgencyLureDetected },
            { label: 'Tech Support Detected', value: d.fraud.techSupportDetected },
            { label: 'Fake Refund Detected', value: d.fraud.fakeRefundDetected },
            { label: 'Misleading Ad Source', value: d.fraud.misleadingAdSource },
          ]
        })
      }

      if (d.lead) {
        sections.push({
          title: 'Lead',
          fields: [
            { label: 'Source Channel', value: d.lead.sourceChannel },
            { label: 'Transfer at Origin', value: d.lead.transferAtOrigin },
            { label: 'Transfer at Origin Confidence', value: d.lead.transferAtOriginConfidence },
            { label: 'Transfer at Origin Phrases', value: d.lead.transferAtOriginPhrases },
            { label: 'Outbound Claim', value: d.lead.outboundClaim },
            { label: 'Outbound Claim Confidence', value: d.lead.outboundClaimConfidence },
            { label: 'Source Evidence', value: d.lead.sourceEvidence },
            { label: 'Outbound Evidence', value: d.lead.outboundEvidence },
          ]
        })
      }

      if (d.agentPerformance) {
        sections.push({
          title: 'Agent Performance',
          fields: [
            { label: 'Fraud Detected', value: d.agentPerformance.fraudDetected },
            { label: 'Professional', value: d.agentPerformance.isProfessional },
            { label: 'Aggression', value: d.agentPerformance.hasAggression },
            { label: 'Connected Quickly', value: d.agentPerformance.connectedQuickly },
            { label: 'Could Hear Caller', value: d.agentPerformance.couldHearCaller },
            { label: 'Asked Caller to Repeat', value: d.agentPerformance.askedCallerToRepeat },
            { label: 'Qualified Promptly', value: d.agentPerformance.qualifiedPromptly },
            { label: 'Asked Qualifying Questions', value: d.agentPerformance.askedQualifyingQuestions },
            { label: 'Qualifications Met', value: d.agentPerformance.qualificationsMet },
            { label: 'Continued After Qualification', value: d.agentPerformance.continuedAfterQualification },
            { label: 'Qualifying Questions Asked', value: d.agentPerformance.qualifyingQuestionsAskedList },
            { label: 'Intent Fulfilled', value: d.agentPerformance.intentFulfilled },
          ]
        })
      }

      if (d.callerExperience) {
        sections.push({
          title: 'Caller Experience',
          fields: [
            { label: 'Understood Intent', value: d.callerExperience.understoodIntent },
            { label: 'Could Hear Agent', value: d.callerExperience.couldHearAgent },
            { label: 'Asked Agent to Repeat', value: d.callerExperience.askedAgentToRepeat },
            { label: 'Was Transferred in Call', value: d.callerExperience.wasTransferredInCall },
            { label: 'Transfer Destination', value: d.callerExperience.wasTransferredInCallDestination },
            { label: 'Transfer Reasoning', value: d.callerExperience.wasTransferredInCallReasoning },
            { label: 'Complained', value: d.callerExperience.complained },
            { label: 'Complaint Phrases', value: d.callerExperience.complaintPhrases },
          ]
        })
      }

      if (d.compliance) {
        sections.push({
          title: 'Compliance',
          fields: [
            { label: 'Recording Disclosure Present', value: d.compliance.recordingDisclosurePresent },
            { label: 'Disclosure Timestamp (sec)', value: d.compliance.disclosureTimestampSec },
            { label: 'Misleading Claims', value: d.compliance.misleadingClaims },
            { label: 'Claim Types', value: d.compliance.claimTypes },
            { label: 'Opt Out Requested', value: d.compliance.optOutRequested },
            { label: 'TCPA Risk', value: d.compliance.tcpaRisk },
          ]
        })
      }

      if (d.routing) {
        sections.push({
          title: 'Routing',
          fields: [
            { label: 'Answered by Person', value: d.routing.answeredByPerson },
            { label: 'Time to Human (sec)', value: d.routing.timeToHumanSec },
            { label: 'Hold Duration (sec)', value: d.routing.holdDurationSec },
            { label: 'Dead Air Total (sec)', value: d.routing.deadAirTotalSec },
            { label: 'Voicemail/Unavailable', value: d.routing.vmailOrUnavailable },
            { label: 'Repetitive Audio Detected', value: d.routing.repetitiveAudioDetected },
          ]
        })
      }

      if (d.conversion) {
        sections.push({
          title: 'Conversion',
          fields: [
            { label: 'Outcome', value: d.conversion.outcome },
            { label: 'Outcome Confidence', value: d.conversion.outcomeConfidence },
            { label: 'Intent Signals', value: d.conversion.intentSignals },
            { label: 'Objection Types', value: d.conversion.objectionTypes },
            { label: 'Agent to Close Latency (sec)', value: d.conversion.agentToCloseLatencySec },
          ]
        })
      }

      if (d.buffer) {
        sections.push({
          title: 'Buffer',
          fields: [
            { label: 'Proximity Exit', value: d.buffer.proximityExit },
            { label: 'Prebuffer Callback Offer', value: d.buffer.prebufferCallbackOffer },
            { label: 'Prebuffer Callback Phrase', value: d.buffer.prebufferCallbackPhrase },
            { label: 'Suspicious Pattern Score', value: d.buffer.suspiciousPatternScore },
          ]
        })
      }

      if (d.sentimentEngagement) {
        sections.push({
          title: 'Sentiment & Engagement',
          fields: [
            { label: 'Caller Level', value: d.sentimentEngagement.callerLevel },
            { label: 'Agent Level', value: d.sentimentEngagement.agentLevel },
            { label: 'Overlaps/Crosstalk Count', value: d.sentimentEngagement.overlapsCrosstalkCount },
            { label: 'Avg Agent Monologue (sec)', value: d.sentimentEngagement.avgAgentMonologueSec },
            { label: 'Avg Caller Monologue (sec)', value: d.sentimentEngagement.avgCallerMonologueSec },
          ]
        })
      }

      if (d.audio) {
        sections.push({
          title: 'Audio',
          fields: [
            { label: 'Silence Gaps Total (sec)', value: d.audio.silenceGapsTotalSec },
            { label: 'Connection Drop Detected', value: d.audio.connectionDropDetected },
          ]
        })
      }

      if (d.pii) {
        sections.push({
          title: 'PII',
          fields: [
            { label: 'Detected Types', value: d.pii.detectedTypes },
            { label: 'Redaction Recommended', value: d.pii.redactionRecommended },
          ]
        })
      }

      if (d.locale) {
        sections.push({
          title: 'Locale',
          fields: [
            { label: 'Detected Language', value: d.locale.detectedLanguage },
            { label: 'Accent Hint', value: d.locale.accentHint },
          ]
        })
      }

      if (d.geo) {
        sections.push({
          title: 'Geo',
          fields: [
            { label: 'Geo Mentions', value: d.geo.geoMentions },
            { label: 'Buyer Region Fit', value: d.geo.buyerRegionFit },
          ]
        })
      }

      if (d.ivr) {
        sections.push({
          title: 'IVR',
          fields: [
            { label: 'Detected', value: d.ivr.detected },
            { label: 'Repetition Segments Total (sec)', value: d.ivr.repetitionSegmentsTotalSec },
            { label: 'Path Indicators', value: d.ivr.pathIndicators },
          ]
        })
      }

      if (d.businessClassification) {
        sections.push({
          title: 'Business Classification',
          fields: [
            { label: 'Vertical', value: d.businessClassification.vertical },
            { label: 'Sub Vertical', value: d.businessClassification.subVertical },
            { label: 'Product', value: d.businessClassification.product },
            { label: 'Confidence', value: d.businessClassification.confidence },
            { label: 'Is New Discovery', value: d.businessClassification.isNewDiscovery },
          ]
        })
      }

      return sections
    }
  },

  async created() {
    await this._fetchDetail()
  },

  methods: {
    ...mapActions('frauds', ['fetchResultById']),

    async _fetchDetail() {
      this.loading = true
      this.error = null
      try {
        const data = await this.fetchResultById(this.$route.params.id)
        if (data?.analyticsData) {
          data.analyticsData = this._convertKeysToCamel(data.analyticsData)
        }

        this.detail = data
      } catch (e) {
        this.error = 'Failed to load call detail'
        showErrorMessage(this.error)
        console.error(e)
      } finally {
        this.loading = false
      }
    },

    _convertKeysToCamel(obj) {
      if (Array.isArray(obj)) {
        return obj.map(item => this._convertKeysToCamel(item))
      }

      if (obj !== null && typeof obj === 'object') {
        return Object.keys(obj).reduce((acc, key) => {
          const camelKey = key.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase())
          acc[camelKey] = this._convertKeysToCamel(obj[key])
          return acc
        }, {})
      }

      return obj
    },

    formatDateTime(date) {
      return moment(date).format('MMM DD, YYYY, h:mm A')
    },

    formatTag(tag) {
      return tag.replace(/_/g, ' ')
    },

    tagDescription(tag) {
      return FILTER_TAG_DESCRIPTIONS[tag] || ''
    },

    splitTags(tags) {
      if (!tags) {
        return []
      }

      return tags.split(',').map(t => t.trim()).filter(t => t)
    },
  },
}
</script>

<style scoped>
.badge-fraud-count {
  background-color: rgba(229, 83, 83, 0.15);
  color: #d93737;
}
.badge-suspicious-count {
  background-color: rgba(249, 177, 21, 0.2);
  color: #c48b00;
}
</style>
